// src/pages/admin/ReportsPage.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import Select from 'react-select'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { th } from 'date-fns/locale/th'
import { Rocket, Filter, Book, Search, Calendar, RefreshCcw, Users, Layers, BarChart2, ChevronRight, XCircle, GraduationCap, Library, ClipboardList, Printer, FileDown } from 'lucide-react'
import DataTable from '@/widgets/DataTable'
import api, { getClasses, getCourses, getSubjects, getTests } from '@/api/kuizy'
import { formatDateTime } from '@/settings/master'

registerLocale('th', th)

const StatusBadge = ({ value }) => {
  const v = String(value || '').toLowerCase()
  const map = {
    running: { text: 'กำลังสอบ', cls: 'bg-emerald-100 text-emerald-700' },
    scheduled: { text: 'ยังไม่เริ่ม', cls: 'bg-blue-100 text-blue-700' },
    ended: { text: 'สิ้นสุดแล้ว', cls: 'bg-gray-100 text-gray-700' },
    inactive: { text: 'ไม่ได้ใช้งาน', cls: 'bg-orange-100 text-orange-700' },
    deleted: { text: 'ไม่ได้ใช้งาน', cls: 'bg-red-100 text-red-700' },
    unknown: { text: 'ไม่ได้ใช้งาน', cls: 'bg-orange-100 text-orange-700' },
    '': { text: '-', cls: 'bg-gray-100 text-gray-600' }
  }
  const m = map[v] || map['unknown']
  return <span className={`px-2 py-1 rounded-full text-xs ${m.cls}`}>{m.text}</span>
}

export default function ReportsPage() {
  const [view, setView] = useState('class')
  const [classrooms, setClassrooms] = useState([])
  const [courses, setCourses] = useState([])
  const [contents, setContents] = useState([])
  const [tests, setTests] = useState([])
  const [classId, setClassId] = useState('')
  const [courseId, setCourseId] = useState('')
  const [contentId, setContentId] = useState('')
  const [testId, setTestId] = useState('')
  const [dateFrom, setDateFrom] = useState(null)
  const [dateTo, setDateTo] = useState(null)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(() => {
    const v = localStorage.getItem('reports_per_page')
    return v ? parseInt(v, 10) : 10
  })
  const [sortBy, setSortBy] = useState('updated_at')
  const [sortDir, setSortDir] = useState('desc')
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches
  const refreshingRef = useRef(false)

  const fmtScore = v => typeof v === 'number' && !Number.isNaN(v) ? v.toFixed(2) : '-'
  const pct = v => (typeof v === 'number' && !Number.isNaN(v)) ? `${v.toFixed(1)}%` : '-'
  const statusText = v => {
    const m = String(v || '').toLowerCase()
    if (m === 'running') return 'กำลังสอบ'
    if (m === 'scheduled') return 'ยังไม่เริ่ม'
    if (m === 'ended') return 'สิ้นสุดแล้ว'
    if (m === 'inactive') return 'ไม่ได้ใช้งาน'
    if (m === 'deleted') return 'ไม่ได้ใช้งาน'
    if (m === 'unknown') return 'ไม่ได้ใช้งาน'
    return '-'
  }

  const classOptions = useMemo(
    () => [{ value: '', label: 'ทุกห้องเรียน' }, ...classrooms.map(c => ({ value: String(c.id || c.class_id), label: c.name || c.class_name }))],
    [classrooms]
  )
  const courseOptions = useMemo(
    () => [{ value: '', label: 'ทุกหลักสูตร' }, ...courses.map(c => ({ value: String(c.id || c.course_id), label: c.name || c.course_name }))],
    [courses]
  )
  const contentOptions = useMemo(
    () => [{ value: '', label: 'ทุกเนื้อหา' }, ...contents.map(s => ({ value: String(s.id || s.content_id), label: s.name || s.content_name || s.code }))],
    [contents]
  )
  const testOptions = useMemo(
    () => [{ value: '', label: 'ทุกชุดข้อสอบ' }, ...tests.map(t => ({ value: String(t.id || t.test_id), label: t.name || t.test_name }))],
    [tests]
  )

  const level = useMemo(() => {
    if (testId) return 'learners'
    if (contentId) return 'tests'
    if (view === 'class') {
      if (courseId) return 'contents'
      if (classId) return 'courses'
      return 'classes'
    }
    if (view === 'course') {
      if (courseId && classId) return 'contents'
      if (courseId) return 'courseClasses'
      return 'coursesTop'
    }
    return 'coursesTop'
  }, [view, classId, courseId, contentId, testId])

  const title = useMemo(() => ({
    classes: 'รายงานตามห้องเรียน',
    courses: 'หลักสูตรของห้องเรียน',
    courseClasses: 'ห้องเรียนของหลักสูตร',
    coursesTop: 'รายงานตามหลักสูตร',
    contents: 'รายงานเนื้อหา',
    tests: 'รายงานชุดข้อสอบ',
    learners: 'ผลสอบผู้เรียน'
  }[level]), [level])

  const columns = useMemo(() => {
    if (level === 'classes') return [
      { key: '__no', title: '#', align: 'left', sortable: false, render: (_, i) => (page - 1) * perPage + i + 1 },
      { key: 'class_name', title: 'ห้องเรียน', sortable: true, render: r => <button className="text-primary font-semibold hover:underline" onClick={() => { setCourseId(''); setContentId(''); setTestId(''); setClassId(String(r.class_id || r.id)) }}>{r.class_name}</button> },
      { key: 'status', title: 'สถานะ', align: 'center', sortable: true, render: r => <StatusBadge value={r.status} /> },
      { key: 'learner_count', title: 'ผู้เรียน', align: 'center', sortable: true, render: r => r.learner_count ?? r.learners ?? 0 },
      { key: 'attempt_count', title: 'จำนวนสอบ', align: 'center', sortable: true, render: r => r.attempt_count ?? r.attempts ?? 0 },
      { key: 'avg_score', title: 'คะแนนเฉลี่ย', align: 'right', sortable: true, render: r => fmtScore(r.avg_score ?? r.avg_score_pct) },
      { key: 'pass_rate', title: 'อัตราผ่าน', align: 'right', sortable: true, render: r => pct(r.pass_rate) },
      { key: 'updated_at', title: 'อัปเดตล่าสุด', align: 'right', sortable: true, render: r => formatDateTime(r.updated_at || r.last_activity) }
    ]
    if (level === 'courses') return [
      { key: '__no', title: '#', align: 'left', sortable: false, render: (_, i) => (page - 1) * perPage + i + 1 },
      { key: 'course_name', title: 'หลักสูตร', sortable: true, render: r => <button className="text-primary font-semibold hover:underline" onClick={() => { setContentId(''); setTestId(''); setCourseId(String(r.course_id || r.id)) }}>{r.course_name}</button> },
      { key: 'status', title: 'สถานะ', align: 'center', sortable: true, render: r => <StatusBadge value={r.status} /> },
      { key: 'content_count', title: 'เนื้อหา', align: 'center', sortable: true, render: r => r.content_count ?? r.contents_count ?? 0 },
      { key: 'attempt_count', title: 'จำนวนสอบ', align: 'center', sortable: true, render: r => r.attempt_count ?? r.attempts ?? 0 },
      { key: 'avg_score', title: 'คะแนนเฉลี่ย', align: 'right', sortable: true, render: r => fmtScore(r.avg_score ?? r.avg_score_pct) },
      { key: 'pass_rate', title: 'อัตราผ่าน', align: 'right', sortable: true, render: r => pct(r.pass_rate) },
      { key: 'updated_at', title: 'อัปเดตล่าสุด', align: 'right', sortable: true, render: r => formatDateTime(r.updated_at || r.last_activity) }
    ]
    if (level === 'courseClasses') return [
      { key: '__no', title: '#', align: 'left', sortable: false, render: (_, i) => (page - 1) * perPage + i + 1 },
      { key: 'class_name', title: 'ห้องเรียน', sortable: true, render: r => <button className="text-primary font-semibold hover:underline" onClick={() => { setClassId(String(r.class_id || r.id)); setContentId(''); setTestId('') }}>{r.class_name}</button> },
      { key: 'status', title: 'สถานะ', align: 'center', sortable: true, render: r => <StatusBadge value={r.status} /> },
      { key: 'learner_count', title: 'ผู้เรียน', align: 'center', sortable: true, render: r => r.learner_count ?? 0 },
      { key: 'attempt_count', title: 'จำนวนสอบ', align: 'center', sortable: true, render: r => r.attempt_count ?? 0 },
      { key: 'avg_score', title: 'คะแนนเฉลี่ย', align: 'right', sortable: true, render: r => fmtScore(r.avg_score) },
      { key: 'pass_rate', title: 'อัตราผ่าน', align: 'right', sortable: true, render: r => pct(r.pass_rate) },
      { key: 'updated_at', title: 'อัปเดตล่าสุด', align: 'right', sortable: true, render: r => formatDateTime(r.updated_at) }
    ]
    if (level === 'coursesTop') return [
      { key: '__no', title: '#', align: 'left', sortable: false, render: (_, i) => (page - 1) * perPage + i + 1 },
      { key: 'course_name', title: 'หลักสูตร', sortable: true, render: r => <button className="text-primary font-semibold hover:underline" onClick={() => { setView('course'); setCourseId(String(r.course_id || r.id)); setClassId(''); setContentId(''); setTestId('') }}>{r.course_name}</button> },
      { key: 'status', title: 'สถานะ', align: 'center', sortable: true, render: r => <StatusBadge value={r.status} /> },
      { key: 'class_count', title: 'จำนวนห้องเรียน', align: 'center', sortable: true, render: r => r.class_count ?? r.classes_count ?? 0 },
      { key: 'attempt_count', title: 'จำนวนสอบ', align: 'center', sortable: true, render: r => r.attempt_count ?? r.attempts ?? 0 },
      { key: 'avg_score', title: 'คะแนนเฉลี่ย', align: 'right', sortable: true, render: r => fmtScore(r.avg_score ?? r.avg_score_pct) },
      { key: 'pass_rate', title: 'อัตราผ่าน', align: 'right', sortable: true, render: r => pct(r.pass_rate) },
      { key: 'updated_at', title: 'อัปเดตล่าสุด', align: 'right', sortable: true, render: r => formatDateTime(r.updated_at || r.last_activity) }
    ]
    if (level === 'contents') return [
      { key: '__no', title: '#', align: 'left', sortable: false, render: (_, i) => (page - 1) * perPage + i + 1 },
      { key: 'content_name', title: 'เนื้อหา', sortable: true, render: r => <button className="text-primary font-semibold hover:underline" onClick={() => { setTestId(''); setContentId(String(r.content_id || r.id)) }}>{r.content_name || r.name || r.code}</button> },
      { key: 'status', title: 'สถานะ', align: 'center', sortable: true, render: r => <StatusBadge value={r.status} /> },
      { key: 'test_count', title: 'ชุดข้อสอบ', align: 'center', sortable: true, render: r => r.test_count ?? r.tests_count ?? 0 },
      { key: 'attempt_count', title: 'จำนวนสอบ', align: 'center', sortable: true, render: r => r.attempt_count ?? r.attempts ?? 0 },
      { key: 'avg_score', title: 'คะแนนเฉลี่ย', align: 'right', sortable: true, render: r => fmtScore(r.avg_score ?? r.avg_score_pct) },
      { key: 'pass_rate', title: 'อัตราผ่าน', align: 'right', sortable: true, render: r => pct(r.pass_rate) },
      { key: 'updated_at', title: 'อัปเดตล่าสุด', align: 'right', sortable: true, render: r => formatDateTime(r.updated_at || r.last_activity) }
    ]
    if (level === 'tests') return [
      { key: '__no', title: '#', align: 'left', sortable: false, render: (_, i) => (page - 1) * perPage + i + 1 },
      { key: 'test_name', title: 'ชุดข้อสอบ', sortable: true, render: r => <button className="text-primary font-semibold hover:underline" onClick={() => { setTestId(String(r.test_id || r.id)) }}>{r.test_name || r.name}</button> },
      { key: 'status', title: 'สถานะ', align: 'center', sortable: true, render: r => <StatusBadge value={r.status} /> },
      { key: 'attempt_count', title: 'จำนวนสอบ', align: 'center', sortable: true, render: r => r.attempt_count ?? r.attempts ?? 0 },
      { key: 'avg_score', title: 'คะแนนเฉลี่ย', align: 'right', sortable: true, render: r => fmtScore(r.avg_score ?? r.avg_score_pct) },
      { key: 'pass_rate', title: 'อัตราผ่าน', align: 'right', sortable: true, render: r => pct(r.pass_rate) },
      { key: 'start_time', title: 'เริ่ม', align: 'right', sortable: true, render: r => formatDateTime(r.start_time) },
      { key: 'end_time', title: 'สิ้นสุด', align: 'right', sortable: true, render: r => formatDateTime(r.end_time) }
    ]
    return [
      { key: '__no', title: '#', align: 'left', sortable: false, render: (_, i) => (page - 1) * perPage + i + 1 },
      { key: 'full_name', title: 'ผู้เรียน', sortable: true, render: r => <span>{r.full_name || `${r.first_name || ''} ${r.last_name || ''}`}</span> },
      { key: 'email', title: 'อีเมล', sortable: true },
      { key: 'group_names', title: 'กลุ่ม', align: 'center', sortable: false, render: r => Array.isArray(r.group_names) ? r.group_names.join(', ') : (r.group_names || '-') },
      { key: 'attempts', title: 'ครั้งที่สอบ', align: 'center', sortable: true },
      { key: 'last_score', title: 'คะแนนล่าสุด', align: 'right', sortable: true, render: r => fmtScore(r.last_score) },
      { key: 'avg_score', title: 'คะแนนเฉลี่ย', align: 'right', sortable: true, render: r => fmtScore(r.avg_score) },
      { key: 'passed', title: 'ผ่านล่าสุด', align: 'center', sortable: true, render: r => r.passed ? <span className="text-green-600">ผ่าน</span> : <span className="text-red-500">ไม่ผ่าน</span> },
      { key: 'last_time', title: 'เวลาล่าสุด', align: 'right', sortable: true, render: r => formatDateTime(r.last_time) }
    ]
  }, [level, page, perPage])

  const fetchFilters = useCallback(async () => {
    const [cls, crs, subs] = await Promise.all([getClasses(), getCourses({ status: 'active' }), getSubjects()])
    const clsRows = Array.isArray(cls.data) ? cls.data : cls.data?.rows || []
    const crsRows = Array.isArray(crs.data) ? crs.data : crs.data?.rows || []
    const subRows = Array.isArray(subs.data) ? subs.data : subs.data?.rows || []
    setClassrooms(clsRows)
    setCourses(crsRows)
    setContents(subRows)
  }, [])

  const fetchTests = useCallback(async () => {
    if (!contentId) { setTests([]); return }
    const r = await getTests({ content_id: contentId })
    const arr = Array.isArray(r.data) ? r.data : r.data?.rows || []
    setTests(arr)
  }, [contentId])

  const buildParams = useCallback((overrides = {}) => {
    return {
      view,
      class_id: classId || undefined,
      course_id: courseId || undefined,
      content_id: contentId || undefined,
      test_id: testId || undefined,
      date_from: dateFrom?.toISOString(),
      date_to: dateTo?.toISOString(),
      search: search || undefined,
      page,
      per_page: perPage,
      sort_by: sortBy,
      sort_dir: sortDir,
      ...overrides
    }
  }, [view, classId, courseId, contentId, testId, dateFrom, dateTo, search, page, perPage, sortBy, sortDir])

  const fetchRows = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get('/reports/summary', { params: buildParams() })
      const d = res.data || {}
      setRows(d.rows || [])
      setTotal(d.total ?? (Array.isArray(d) ? d.length : 0))
    } catch {
      setRows([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [buildParams])

  const fetchAllRows = useCallback(async () => {
    const res = await api.get('/reports/summary', { params: buildParams({ page: 1, per_page: 100000 }) })
    const d = res.data || {}
    return d.rows || []
  }, [buildParams])

  const statusize = v => statusText(v)
  const displayValue = (r, key) => {
    if (key === 'status') return statusize(r.status)
    if (key === 'class_name') return r.class_name || r.name
    if (key === 'course_name') return r.course_name || r.name
    if (key === 'content_name') return r.content_name || r.name || r.code
    if (key === 'test_name') return r.test_name || r.name
    if (key === 'learner_count') return r.learner_count ?? r.learners ?? 0
    if (key === 'class_count') return r.class_count ?? r.classes_count ?? 0
    if (key === 'content_count') return r.content_count ?? r.contents_count ?? 0
    if (key === 'test_count') return r.test_count ?? r.tests_count ?? 0
    if (key === 'attempt_count') return r.attempt_count ?? r.attempts ?? 0
    if (key === 'avg_score' || key === 'avg_score_pct') return fmtScore(r.avg_score ?? r.avg_score_pct)
    if (key === 'pass_rate') return pct(r.pass_rate)
    if (key === 'last_score') return fmtScore(r.last_score)
    if (key === 'group_names') return Array.isArray(r.group_names) ? r.group_names.join(', ') : (r.group_names || '-')
    if (key === 'updated_at' || key === 'last_activity') return formatDateTime(r.updated_at || r.last_activity)
    if (key === 'start_time') return formatDateTime(r.start_time)
    if (key === 'end_time') return formatDateTime(r.end_time)
    if (key === 'last_time') return formatDateTime(r.last_time)
    if (key === 'full_name') return r.full_name || `${r.first_name || ''} ${r.last_name || ''}`.trim()
    return r[key] ?? ''
  }

  const exportCSV = useCallback(async () => {
    const all = await fetchAllRows()
    const cols = columns.filter(c => c.key && c.key !== '__no')
    const header = cols.map(c => `"${String(c.title).replace(/"/g, '""')}"`).join(',')
    const lines = all.map((r) => cols.map(c => {
      const val = displayValue(r, c.key)
      const s = val == null ? '' : String(val)
      const needsQuote = /[",\n]/.test(s)
      const esc = s.replace(/"/g, '""')
      return needsQuote ? `"${esc}"` : esc
    }).join(','))
    const csv = [header, ...lines].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${title.replace(/\s+/g,'_')}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [columns, fetchAllRows, title])

  const printPDF = useCallback(async () => {
    const all = await fetchAllRows()
    const cols = columns.filter(c => c.key && c.key !== '__no')
    const viewTxt = view === 'class' ? 'มุมมองห้องเรียน' : 'มุมมองหลักสูตร'
    const classTxt = classId ? (classOptions.find(o => o.value === classId)?.label || classId) : 'ทุกห้องเรียน'
    const courseTxt = courseId ? (courseOptions.find(o => o.value === courseId)?.label || courseId) : 'ทุกหลักสูตร'
    const contentTxt = contentId ? (contentOptions.find(o => o.value === contentId)?.label || contentId) : 'ทุกเนื้อหา'
    const testTxt = testId ? (testOptions.find(o => o.value === testId)?.label || testId) : 'ทุกชุดข้อสอบ'
    const dateTxt = `${dateFrom ? formatDateTime(dateFrom) : '-'} ถึง ${dateTo ? formatDateTime(dateTo) : '-'}`
    const searchTxt = search || '-'
    const ths = cols.map(c => `<th style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:${c.align==='right'?'right':c.align==='center'?'center':'left'}">${c.title}</th>`).join('')
    const trs = all.map((r,i) => {
      const tds = cols.map(c => {
        const val = displayValue(r, c.key)
        const align = c.align==='right'?'right':c.align==='center'?'center':'left'
        return `<td style="padding:6px 10px;border-bottom:1px solid #f1f5f9;text-align:${align}">${val == null ? '' : String(val)}</td>`
      }).join('')
      return `<tr><td style="padding:6px 10px;border-bottom:1px solid #f1f5f9">${i+1}</td>${tds}</tr>`
    }).join('')
    const html = `
      <html>
        <head>
          <meta charSet="utf-8" />
          <title>${title}</title>
          <style>
            @page { size: A4 landscape; margin: 14mm; }
            body { font-family: ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial; color: #111827; }
            h1 { font-size: 18px; margin: 0 0 8px 0; }
            .muted { color:#6b7280; font-size: 12px }
            .chip { display:inline-block; background:#f3f4f6; padding:4px 8px; border-radius:999px; margin-right:6px; font-size:12px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            thead th { background:#f8fafc; }
            .header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:12px }
            .left .chips { margin-top:6px }
            .right { text-align:right }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="left">
              <h1>${title}</h1>
              <div class="chips">
                <span class="chip">${viewTxt}</span>
                <span class="chip">ห้องเรียน: ${classTxt}</span>
                <span class="chip">หลักสูตร: ${courseTxt}</span>
                <span class="chip">เนื้อหา: ${contentTxt}</span>
                <span class="chip">ชุดข้อสอบ: ${testTxt}</span>
              </div>
              <div class="muted">ช่วงวันที่: ${dateTxt} | ค้นหา: ${searchTxt}</div>
            </div>
            <div class="right muted">ออกรายงาน: ${formatDateTime(new Date().toISOString())}<br/>จำนวนทั้งหมด: ${total} รายการ</div>
          </div>
          <table>
            <thead>
              <tr><th style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:left">#</th>${ths}</tr>
            </thead>
            <tbody>
              ${trs}
            </tbody>
          </table>
        </body>
      </html>
    `
    const w = window.open('', '_blank')
    if (!w) return
    w.document.open()
    w.document.write(html)
    w.document.close()
    w.focus()
    w.print()
  }, [columns, fetchAllRows, title, view, classId, courseId, contentId, testId, classOptions, courseOptions, contentOptions, testOptions, dateFrom, dateTo, search, total])

  useEffect(() => { fetchFilters() }, [fetchFilters])
  useEffect(() => { fetchTests() }, [fetchTests])
  useEffect(() => {
    setPage(1)
    fetchRows()
  }, [view, classId, courseId, contentId, testId, dateFrom, dateTo, search, perPage, sortBy, sortDir, fetchRows])
  useEffect(() => { localStorage.setItem('reports_per_page', String(perPage)) }, [perPage])

  const resetDrill = () => { setTestId(''); setContentId(''); setClassId(''); setCourseId('') }
  const resetDeep = (lvl) => {
    if (lvl === 'class') { setCourseId(''); setContentId(''); setTestId('') }
    if (lvl === 'course') { setClassId(''); setContentId(''); setTestId('') }
    if (lvl === 'content') { setTestId('') }
  }

  const breadcrumb = (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-gray-700">
        {view === 'class' ? 'มุมมองห้องเรียน' : 'มุมมองหลักสูตร'}
      </span>
      {view === 'class' && classId && <>
        <ChevronRight size={14} />
        <button className="px-2 py-1 rounded-full bg-primary/10 text-primary" onClick={() => { resetDeep('class') }}>
          ห้อง: {classOptions.find(o => o.value === classId)?.label || classId}
        </button>
      </>}
      {courseId && <>
        <ChevronRight size={14} />
        <button className="px-2 py-1 rounded-full bg-secondary/10 text-secondary" onClick={() => { resetDeep('course') }}>
          หลักสูตร: {courseOptions.find(o => o.value === courseId)?.label || courseId}
        </button>
      </>}
      {view === 'course' && classId && <>
        <ChevronRight size={14} />
        <button className="px-2 py-1 rounded-full bg-primary/10 text-primary" onClick={() => { setContentId(''); setTestId(''); setClassId('') }}>
          ห้อง: {classOptions.find(o => o.value === classId)?.label || classId}
        </button>
      </>}
      {contentId && <>
        <ChevronRight size={14} />
        <button className="px-2 py-1 rounded-full bg-purple-100 text-purple-700" onClick={() => { resetDeep('content') }}>
          เนื้อหา: {contentOptions.find(o => o.value === contentId)?.label || contentId}
        </button>
      </>}
      {testId && <>
        <ChevronRight size={14} />
        <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">
          ชุดข้อสอบ: {testOptions.find(o => o.value === testId)?.label || testId}
        </span>
      </>}
      {(classId || courseId || contentId || testId) && <button onClick={resetDrill} className="ml-2 inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-xs"><XCircle size={14} /> ล้างการเจาะลึก</button>}
    </div>
  )

  const ClassSelect = (
    <div className="flex flex-col gap-1">
      <label className="flex items-center gap-1 text-sm"><GraduationCap  size={16} /> ห้องเรียน</label>
      <Select classNamePrefix="rs" options={classOptions} value={classOptions.find(o => o.value === classId)} onChange={o => { setClassId(o.value); if (view !== 'class' && o.value && courseId) setView('course'); if (view !== 'class' && o.value && !courseId) setView('class') }} isSearchable />
    </div>
  )

  const CourseSelect = (
    <div className="flex flex-col gap-1">
      <label className="flex items-center gap-1 text-sm"><Library size={16} /> หลักสูตร</label>
      <Select classNamePrefix="rs" options={courseOptions} value={courseOptions.find(o => o.value === courseId)} onChange={o => { setCourseId(o.value); if (view !== 'course' && o.value && !classId) setView('course') }} isSearchable />
    </div>
  )

  return (
    <div className="flex flex-col gap-6 p-4 sm:p-6 max-w-full">
      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .35 }} className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-secondary/10 to-purple-100 border border-white/30">
        <div className="p-5 sm:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 grid place-items-center rounded-2xl bg-white shadow-sm w-12 h-12">
              <BarChart2 className="text-primary" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900">{title}</h1>
              <p className="text-sm text-gray-600">สรุปผลสอบผู้เรียน แยกตามห้องเรียน/หลักสูตร เจาะลึกถึงชุดข้อสอบและผู้เรียน</p>
              <div className="mt-2">{breadcrumb}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={async () => {
                if (refreshingRef.current) return
                refreshingRef.current = true
                await fetchRows()
                refreshingRef.current = false
              }}
              className="inline-flex items-center gap-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl px-3 py-2 text-sm"
            >
              <RefreshCcw size={16} /> โหลดใหม่
            </button>
            <button
              onClick={printPDF}
              className="inline-flex items-center gap-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl px-3 py-2 text-sm"
            >
              <Printer size={16} /> พิมพ์ PDF
            </button>
            <button
              onClick={exportCSV}
              className="inline-flex items-center gap-2 bg-primary text-white hover:bg-secondary rounded-xl px-3 py-2 text-sm"
            >
              <FileDown size={16} /> ส่งออก CSV
            </button>
          </div>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .35, delay: .04 }} className="bg-white rounded-xl shadow-md">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5 p-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setView('class'); resetDrill() }}
              className={`px-3 py-2 rounded-lg text-sm border ${view === 'class' ? 'bg-primary text-white border-primary' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            >
              ตามห้องเรียน
            </button>
            <button
              onClick={() => { setView('course'); resetDrill() }}
              className={`px-3 py-2 rounded-lg text-sm border ${view === 'course' ? 'bg-secondary text-white border-secondary' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            >
              ตามหลักสูตร
            </button>
          </div>

          {view === 'class' ? <>
            {ClassSelect}
            {CourseSelect}
          </> : <>
            {CourseSelect}
            {ClassSelect}
          </>}

          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-1 text-sm"><Book size={16} /> เนื้อหา</label>
            <Select classNamePrefix="rs" options={contentOptions} value={contentOptions.find(o => o.value === contentId)} onChange={o => setContentId(o.value)} isSearchable />
          </div>

          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-1 text-sm"><ClipboardList size={16} /> ชุดข้อสอบ</label>
            <Select classNamePrefix="rs" options={testOptions} value={testOptions.find(o => o.value === testId)} onChange={o => setTestId(o.value)} isSearchable />
          </div>

          <div className="flex flex-col gap-1 lg:col-span-2">
            <label className="flex items-center gap-1 text-sm"><Calendar size={16} /> ช่วงวันที่</label>
            <div className="flex gap-2">
              <DatePicker
                locale="th"
                selected={dateFrom}
                onChange={setDateFrom}
                selectsStart
                startDate={dateFrom}
                endDate={dateTo}
                placeholderText="จากวันที่"
                dateFormat="d MMM yyyy"
                className="border border-gray-300 rounded-lg px-3 py-2 w-full text-sm"
                popperClassName="z-[2000]"
                withPortal={isMobile}
              />
              <DatePicker
                locale="th"
                selected={dateTo}
                onChange={setDateTo}
                selectsEnd
                startDate={dateFrom}
                endDate={dateTo}
                minDate={dateFrom}
                placeholderText="ถึงวันที่"
                dateFormat="d MMM yyyy"
                className="border border-gray-300 rounded-lg px-3 py-2 w-full text-sm"
                popperClassName="z-[2000]"
                withPortal={isMobile}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1 lg:col-span-3">
            <label className="flex items-center gap-1 text-sm"><Search size={16} /> ค้นหา</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="พิมพ์ข้อความแล้วกด Enter"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1) } }}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary text-sm flex-1"
              />
              <button onClick={() => { setSearch(searchInput); setPage(1) }} className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm">
                <Search size={16} /> ค้นหา
              </button>
              <button
                onClick={() => {
                  setClassId('')
                  setCourseId('')
                  setContentId('')
                  setTestId('')
                  setDateFrom(null)
                  setDateTo(null)
                  setSearchInput('')
                  setSearch('')
                  setView('class')
                  setPage(1)
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                ล้างค่า
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .35, delay: .06 }} className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Filter size={16} />
            ทั้งหมด {total} รายการ
          </div>
        </div>
        <div className="-mx-4 overflow-x-auto sm:mx-0">
          <DataTable
            columns={columns}
            rows={rows}
            loading={loading}
            total={total}
            page={page}
            perPage={perPage}
            sortBy={sortBy}
            sortDir={sortDir}
            onPageChange={setPage}
            onPerPageChange={n => { setPerPage(n); setPage(1) }}
            onSortChange={(k, d) => { setSortBy(k); setSortDir(d); setPage(1) }}
          />
        </div>
      </motion.div>
    </div>
  )
}
